<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import { AlertType } from '@/interfaces/IAlert';
  import { IMenuQrStickerSettings } from '@/interfaces/IMenuQrStickerSettings';
  import SimpleScrollableModal from '../base/SimpleScrollableModal.vue';
  import {
    MenuQrStickerSettings,
    menuQrStickerDpmmOptions,
    menuQrStickerRotationOptions,
  } from '@/lib/print/MenuQrStickerSettings';
  import { useUi } from '@/composition/use-ui';

  const props = defineProps<{
    legacyBranchId?: number | null;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const ui = useUi();
  const saving = ref(false);
  const formValid = ref(true);
  const menuQrStickerSettings = ref<IMenuQrStickerSettings | null>(null);
  const dpmmOptions = menuQrStickerDpmmOptions.map((value) => ({
    text: `${value} dpmm`,
    value,
  }));
  const rotationOptions = menuQrStickerRotationOptions.map((value) => ({
    text: `${value}°`,
    value,
  }));

  function close() {
    emit('close');
  }

  function buildSettingsPayload(): IMenuQrStickerSettings {
    const settings = menuQrStickerSettings.value!;

    return {
      labelWidthMm: Number(settings.labelWidthMm),
      labelHeightMm: Number(settings.labelHeightMm),
      dpmm: Number(settings.dpmm) as IMenuQrStickerSettings['dpmm'],
      padding: {
        top: Number(settings.padding.top),
        right: Number(settings.padding.right),
        bottom: Number(settings.padding.bottom),
        left: Number(settings.padding.left),
      },
      offsetXMm: Number(settings.offsetXMm),
      offsetYMm: Number(settings.offsetYMm),
      rotation: Number(settings.rotation) as IMenuQrStickerSettings['rotation'],
      qrScale: Number(settings.qrScale),
      titleFontSizeMm: Number(settings.titleFontSizeMm),
      titleGapMm: Number(settings.titleGapMm),
      logoSizeMm: Number(settings.logoSizeMm),
      logoOffsetMm: Number(settings.logoOffsetMm),
      footerWidthMm: Number(settings.footerWidthMm),
      footerOffsetBottomMm: Number(settings.footerOffsetBottomMm),
    };
  }

  function loadSettings() {
    menuQrStickerSettings.value = MenuQrStickerSettings.loadSettings({
      legacyBranchId: props.legacyBranchId,
    });
  }

  function save() {
    if (!menuQrStickerSettings.value) return;

    try {
      saving.value = true;
      menuQrStickerSettings.value = MenuQrStickerSettings.saveSettings(
        buildSettingsPayload()
      );
      close();
      ui.showToast(AlertType.Success, 'Configuración guardada.');
    } catch (error) {
      console.error(error);
      ui.showToast(
        AlertType.Error,
        'No se pudo guardar la configuración de los stickers QR.'
      );
    } finally {
      saving.value = false;
    }
  }

  onMounted(() => {
    loadSettings();
  });
</script>

<template>
  <simple-scrollable-modal>
    <template #title>
      <h3>Configuración stickers QR</h3>
    </template>
    <template #content>
      <div class="menu-qr-sticker-settings-modal">
        <p class="menu-qr-sticker-settings-modal__note">
          Solo afecta este navegador y se comparte para mesas, take away y QR
          genéricos.
        </p>
        <template v-if="menuQrStickerSettings">
          <v-form v-model="formValid" @submit.prevent>
            <section class="menu-qr-sticker-settings-modal__section">
              <h4>Etiqueta</h4>
              <div
                class="menu-qr-sticker-settings-modal__grid menu-qr-sticker-settings-modal__grid--4"
              >
                <v-text-field
                  v-model="menuQrStickerSettings.labelWidthMm"
                  label="Ancho (mm)"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
                <v-text-field
                  v-model="menuQrStickerSettings.labelHeightMm"
                  label="Alto (mm)"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
                <v-select
                  v-model="menuQrStickerSettings.dpmm"
                  :items="dpmmOptions"
                  label="Densidad"
                  outlined
                  dense
                  required
                />
                <v-select
                  v-model="menuQrStickerSettings.rotation"
                  :items="rotationOptions"
                  label="Rotación"
                  outlined
                  dense
                  required
                />
              </div>
            </section>

            <section class="menu-qr-sticker-settings-modal__section">
              <h4>Margen interno</h4>
              <div
                class="menu-qr-sticker-settings-modal__grid menu-qr-sticker-settings-modal__grid--4"
              >
                <v-text-field
                  v-model="menuQrStickerSettings.padding.top"
                  label="Arriba"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
                <v-text-field
                  v-model="menuQrStickerSettings.padding.right"
                  label="Derecha"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
                <v-text-field
                  v-model="menuQrStickerSettings.padding.bottom"
                  label="Abajo"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
                <v-text-field
                  v-model="menuQrStickerSettings.padding.left"
                  label="Izquierda"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
              </div>
            </section>

            <section class="menu-qr-sticker-settings-modal__section">
              <h4>Calibración</h4>
              <div
                class="menu-qr-sticker-settings-modal__grid menu-qr-sticker-settings-modal__grid--2"
              >
                <v-text-field
                  v-model="menuQrStickerSettings.offsetXMm"
                  label="Desplazamiento X"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
                <v-text-field
                  v-model="menuQrStickerSettings.offsetYMm"
                  label="Desplazamiento Y"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
              </div>
            </section>

            <section class="menu-qr-sticker-settings-modal__section">
              <h4>Contenido</h4>
              <div
                class="menu-qr-sticker-settings-modal__grid menu-qr-sticker-settings-modal__grid--3"
              >
                <v-text-field
                  v-model="menuQrStickerSettings.qrScale"
                  label="Escala QR"
                  type="number"
                  step="0.05"
                  min="0.2"
                  max="1"
                  outlined
                  dense
                  required
                />
                <v-text-field
                  v-model="menuQrStickerSettings.titleFontSizeMm"
                  label="Texto"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
                <v-text-field
                  v-model="menuQrStickerSettings.titleGapMm"
                  label="Separación texto"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
              </div>
            </section>

            <section class="menu-qr-sticker-settings-modal__section">
              <h4>Elementos auxiliares</h4>
              <div
                class="menu-qr-sticker-settings-modal__grid menu-qr-sticker-settings-modal__grid--4"
              >
                <v-text-field
                  v-model="menuQrStickerSettings.logoSizeMm"
                  label="Tamaño logo"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
                <v-text-field
                  v-model="menuQrStickerSettings.logoOffsetMm"
                  label="Margen logo"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
                <v-text-field
                  v-model="menuQrStickerSettings.footerWidthMm"
                  label="Ancho pie"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
                <v-text-field
                  v-model="menuQrStickerSettings.footerOffsetBottomMm"
                  label="Margen pie"
                  type="number"
                  outlined
                  dense
                  required
                  suffix="mm"
                />
              </div>
            </section>
          </v-form>
        </template>
      </div>
    </template>
    <template #action-bar>
      <div class="menu-qr-sticker-settings-modal__actions">
        <v-btn color="danger" text depressed :disabled="saving" @click="close">
          Cerrar
        </v-btn>
        <v-btn color="info" depressed :loading="saving" @click="save">
          Guardar
        </v-btn>
      </div>
    </template>
  </simple-scrollable-modal>
</template>

<style scoped lang="scss">
  .menu-qr-sticker-settings-modal {
    &__note {
      @apply tw-mb-4 tw-text-sm tw-text-gray-600;
    }

    &__section {
      @apply tw-mt-4;

      h4 {
        @apply tw-mb-2 tw-text-base tw-font-semibold;
      }
    }

    &__grid {
      @apply tw-grid tw-gap-2;

      &--2 {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      &--3 {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }

      &--4 {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
    }

    &__actions {
      @apply tw-flex tw-justify-end tw-gap-2;
    }

    @media (max-width: 960px) {
      &__grid {
        &--2,
        &--3,
        &--4 {
          grid-template-columns: minmax(0, 1fr);
        }
      }
    }
  }
</style>
